// Label + ordering map for the eligibility-form card, keyed by `linkId`.
//
// Deliberately NOT fetched from the canonical Questionnaire:
// `pac-access-policy.json` grants no `Questionnaire` read, and the card
// needs its own map anyway for EN/ES.
//
// Answer labels are transcribed from ELIGIBILITY_STEPS in bariendo-be
// `src/features/eligibility/eligibility-steps.config.ts` (origin/main) so the
// card reads back the same words the lead was shown. Two coding conventions
// coexist on purpose: codes passed through from backend enums keep their source
// form (`ELIGIBLE`, `AGE_UNDER_18`), while codes this design coined are kebab
// (`not-a-fit`) or internal short-forms (`ESG`). Do not assume one style.

/** Left / right column of the expanded card, per the spec's item map. */
export const ELIGIBILITY_COLUMNS = {
  left: ['interest-eligibility', 'contact'],
  right: ['payment', 'form-activity'],
} as const;

export const ELIGIBILITY_GROUP_LABELS: Record<string, string> = {
  'interest-eligibility': 'Interest & eligibility',
  contact: 'Contact',
  payment: 'Payment',
  'form-activity': 'Form activity',
};

/**
 * Item order within each group — the render order, independent of how the QR
 * happens to be assembled.
 */
export const ELIGIBILITY_GROUP_ITEMS: Record<string, string[]> = {
  'interest-eligibility': [
    'goal',
    'prior-procedure',
    'conditions',
    'height',
    'weight',
    'bmi',
    'date-of-birth',
    'interested-in',
    'result',
    'ineligible-reason',
  ],
  contact: ['first-name', 'last-name', 'email', 'phone', 'zip', 'nearest-location', 'distance-mi'],
  payment: [
    'payment-mode',
    'employer',
    'employer-covered',
    // BAR-2902: the "Insurance or employer benefit" answers. `coverage-result`
    // is derived by bariendo-be from the result rules, not typed by the lead.
    'coverage-source',
    'insurer',
    'member-id',
    'group-number',
    'subscriber',
    'coverage-result',
  ],
  'form-activity': [
    'final-action',
    'call-slot',
    'reached-step',
    'heard-about-us',
    'track',
    'benefit-administrator',
    'started-at',
    'time-to-complete',
  ],
};

export const ELIGIBILITY_ITEM_LABELS: Record<string, string> = {
  goal: 'Goal',
  'prior-procedure': 'Prior procedure',
  conditions: 'Conditions',
  height: 'Height',
  weight: 'Current weight',
  bmi: 'BMI',
  'date-of-birth': 'Date of birth',
  'interested-in': 'Interested in',
  result: 'Result',
  'ineligible-reason': 'Reason not a fit',
  'first-name': 'First name',
  'last-name': 'Last name',
  email: 'Email',
  phone: 'Phone',
  zip: 'ZIP',
  'nearest-location': 'Nearest location',
  'distance-mi': 'Distance',
  'payment-mode': 'Preference',
  employer: 'Employer',
  'employer-covered': 'Employer benefit check',
  'coverage-source': 'Insurance via',
  insurer: 'Insurer',
  'member-id': 'Member ID',
  'group-number': 'Group number',
  subscriber: 'Subscriber',
  'coverage-result': 'Coverage result',
  'final-action': 'Final action',
  'call-slot': 'Intro call slot',
  'reached-step': 'Reached',
  'heard-about-us': 'Heard about us',
  track: 'Track',
  'benefit-administrator': 'Administered by',
  'started-at': 'Form started',
  'time-to-complete': 'Time to complete',
};

/**
 * `reached-step` is a step-ID string, not an integer screen number — the 17
 * ELIGIBILITY_STEPS ids from the v21 flow, plus the six `coverage-*` ids from the
 * insurance spec (BAR-2902), which bariendo-be does not emit yet.
 * POST_ACCOUNT_STEPS is a transitional parallel set and is deliberately
 * unmapped; unknown ids fall back to a humanised form of the raw id.
 */
export const ELIGIBILITY_STEP_LABELS: Record<string, string> = {
  goal: 'Goal',
  body: 'Height & weight',
  history: 'Prior procedure',
  dob: 'Date of birth',
  condition: 'Conditions',
  ineligible: 'Not a fit',
  plan: 'Plan choice',
  'patient-stories': 'Patient stories',
  compare: 'Compare options',
  'create-account': 'Create account',
  'year-of-care': 'Year of care',
  'payment-options': 'Payment options',
  'coverage-source': 'How they get insurance',
  'coverage-employer': 'Coverage employer',
  'coverage-insurer': 'Insurer',
  'coverage-card': 'Insurance card',
  'coverage-result': 'Coverage result',
  'coverage-declined': 'Not covered yet',
  price: 'Price',
  'schedule-intro-call': 'Schedule intro call',
  'confirm-call': 'Confirm call',
  'call-confirmed': 'Call confirmed',
  attribution: 'How they heard about us',
};

/** Answer-code labels, per item. Codes absent here render as-is. */
export const ELIGIBILITY_ANSWER_LABELS: Record<string, Record<string, string>> = {
  // `track` is deliberately absent: its codes render as stored, unchanged.
  'benefit-administrator': {
    carrum: 'Carrum',
    transcarent: 'Transcarent',
  },
  goal: {
    lasting: 'Find a lasting way to lose weight',
    health: 'Improve my health condition, such as sleep apnea, knee pain, diabetes',
    alt_glp: 'Find an alternative to GLP-1',
    learn_esg: 'Learn more about ESG',
    regain: "Lose weight I've regained after bariatric surgery",
    explore: 'Just explore my options',
  },
  'prior-procedure': {
    none: 'No',
    sleeve: 'Gastric sleeve',
    bypass: 'Gastric bypass',
    balloon: 'Gastric balloon',
    esg_other: 'ESG from another provider',
    other: 'Another procedure including LAP-Band',
  },
  conditions: {
    high_bp: 'High blood pressure',
    diabetes: 'Diabetes',
    high_cholesterol: 'High cholesterol',
    sleep_apnea: 'Sleep apnea',
    fatty_liver_disease: 'Fatty liver disease',
    none: 'None of these',
  },
  'payment-mode': {
    monthly: 'Monthly financing',
    full: 'Pay in full',
    hsa: 'HSA or FSA',
    employer: 'Check employer benefit',
    coverage: 'Insurance or employer benefit',
    unsure: "I'm not sure yet",
  },
  'heard-about-us': {
    social: 'Instagram or Facebook',
    google: 'Google',
    tv: 'TV',
    podcast_radio: 'Podcast or radio',
    friend_family: 'Friend or family',
    provider: 'Doctor or healthcare provider',
    news: 'News article or online publication',
    youtube: 'YouTube',
    other: 'Other',
  },
  result: {
    IN_PROGRESS: 'In progress',
    ELIGIBLE: 'Eligible',
    INELIGIBLE: 'Not a fit',
    EXPIRED: 'Expired',
  },
  'ineligible-reason': {
    AGE_UNDER_18: 'Under 18',
    BMI_TOO_LOW: 'BMI under 27',
    BMI_TOO_HIGH: 'BMI 50 or over',
    NO_QUALIFYING_CONDITION: 'BMI 27-30 with no qualifying condition',
  },
  'final-action': {
    scheduled: 'Scheduled intro call',
    'called-live': 'Called our team live',
    'not-a-fit': 'Not a fit',
  },
  // `interested-in` is deliberately absent: ESG / IGB / ReESG / SIS / TORe are
  // internal short-forms already, and patient-facing names never appear in Orbit.
};

export function getEligibilityItemLabel(linkId: string, fallbackText?: string): string {
  return ELIGIBILITY_ITEM_LABELS[linkId] ?? fallbackText ?? humanise(linkId);
}

export function getEligibilityStepLabel(stepId: string): string {
  return ELIGIBILITY_STEP_LABELS[stepId] ?? humanise(stepId);
}

/** `payment-options` -> `Payment options`. Last resort for unmapped ids. */
export function humanise(id: string): string {
  const spaced = id.replace(/[-_]/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
